import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { InstitutionItemTemplateCategoriesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsInstitutionsSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as InstitutionItemTemplateCategoriesActions from "../../../actions/institutionItemTemplateCategories";
import * as NavigationActions from "../../../actions/navigation";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import {
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_OPTIONS,
} from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { InstitutionLookupsLayout } from "../../index";

import "./InstitutionItemTemplateCategoriesPage.scss";

interface InstitutionItemTemplateCategorysPageProps {
    formFilter: IFormFilter;
    institutionItemTemplateCategory: Dtos.InstitutionItemTemplateCategory[];
    loadingInstitutionItemTemplateCategorys: boolean;
    loadInstitutionItemTemplateCategorysSuccess: boolean;
    loadInstitutionItemTemplateCategorysFailure: boolean;
    loadInstitutionItemTemplateCategorysErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearInstitutionItemTemplateCategorys: InstitutionItemTemplateCategoriesActions.IInstitutionItemTemplateCategoryClearActionCreator;
    loadInstitutionItemTemplateCategorys: InstitutionItemTemplateCategoriesActions.IInstitutionItemTemplateCategoryLoadActionCreator;
    filter: InstitutionItemTemplateCategoriesActions.IInstitutionItemTemplateCategoryFilterActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const InstitutionItemTemplateCategorysPage: FC<
    InstitutionItemTemplateCategorysPageProps
> = ({
    formFilter,
    institutionItemTemplateCategory,
    loadingInstitutionItemTemplateCategorys,
    loadInstitutionItemTemplateCategorysSuccess,
    loadInstitutionItemTemplateCategorysFailure,
    loadInstitutionItemTemplateCategorysErrorCode,
    permissions,
    clearInstitutionItemTemplateCategorys,
    loadInstitutionItemTemplateCategorys,
    filter,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showInstitutionItemTemplateCategorys =
        !loadingInstitutionItemTemplateCategorys &&
        loadInstitutionItemTemplateCategorysSuccess;


    // Load institution item template categories
    useEffect(() => {
        if (!initalised) {
            setDrawerItems(SettingsInstitutionsSubMenu(permissions));
            setBreadcrumbs(InstitutionItemTemplateCategoriesBreadcrumbs());

            loadInstitutionItemTemplateCategorys();

            setInitalised(true);
        }
    }, [
        initalised,
        setDrawerItems,
        SettingsInstitutionsSubMenu,
        permissions,
        setBreadcrumbs,
        InstitutionItemTemplateCategoriesBreadcrumbs,
        loadInstitutionItemTemplateCategorys,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadInstitutionItemTemplateCategorysFailure) {
            navigate(`/error/${loadInstitutionItemTemplateCategorysErrorCode}`);
        }
    }, [
        loadInstitutionItemTemplateCategorysFailure,
        loadInstitutionItemTemplateCategorysErrorCode,
        navigate,
    ]);

    // Cleanup effect to clear the institution item template categories on component unmount
    useEffect(() => {
        return () => {
            clearInstitutionItemTemplateCategorys();
        };
    }, [clearInstitutionItemTemplateCategorys]);

    const createTable = useMemo(() => {
        if (
            !institutionItemTemplateCategory ||
            institutionItemTemplateCategory.length == 0
        ) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no item categories to view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination =
            institutionItemTemplateCategory.length > DEFAULT_PAGE_SIZE;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={institutionItemTemplateCategory}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination
                            ? DEFAULT_PAGE_SIZE
                            : institutionItemTemplateCategory.length
                    }
                    pageSize={
                        showPagination
                            ? undefined
                            : institutionItemTemplateCategory.length
                    }
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no item categories to display"
                    resizable={false}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const url: string = `/settings/institution/itemtemplatecategories/${rowInfo.original.id}`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Light.LOOKUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "value",
                            Header: "Name",
                            accessor: "value",
                        },
                    ]}
                />
            </div>
        );
    }, [
        institutionItemTemplateCategory,
        DEFAULT_PAGE_SIZE,
        PAGE_SIZE_OPTIONS,
        navigate,
    ]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Institution Item Categories</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />

                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.CategoriesCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={
                                    "/settings/institution/itemtemplatecategories/create"
                                }
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showInstitutionItemTemplateCategorys ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showInstitutionItemTemplateCategorys ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [
        formFilter,
        filter,
        permissions,
        showInstitutionItemTemplateCategorys,
        createTable,
    ]);

    return (
        <InstitutionLookupsLayout
            loading={loadingInstitutionItemTemplateCategorys}
            permission={Dtos.Permission.InstitutionItemTemplateCategoriesView}
        >
            {renderContent}
        </InstitutionLookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<InstitutionItemTemplateCategorysPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];
    return {
        formFilter: state.institutionItemTemplateCategories.formFilter,
        institutionItemTemplateCategory:
            state.institutionItemTemplateCategories.formFilteredData,
        loadingInstitutionItemTemplateCategorys:
            state.institutionItemTemplateCategories.loadState &&
            state.institutionItemTemplateCategories.loadState.status ==
                RequestState.Pending,
        loadInstitutionItemTemplateCategorysSuccess:
            state.institutionItemTemplateCategories.loadState &&
            state.institutionItemTemplateCategories.loadState.status ==
                RequestState.Success,
        loadInstitutionItemTemplateCategorysFailure:
            state.institutionItemTemplateCategories.loadState &&
            state.institutionItemTemplateCategories.loadState.status ==
                RequestState.Failure,
        loadInstitutionItemTemplateCategorysErrorCode:
            state.institutionItemTemplateCategories.loadState.errorCode,
        permissions: permissions,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<InstitutionItemTemplateCategorysPageProps> => {
    return {
        loadInstitutionItemTemplateCategorys: bindActionCreators(
            InstitutionItemTemplateCategoriesActions.LoadInstitutionItemTemplateCategory,
            dispatch
        ),
        clearInstitutionItemTemplateCategorys: bindActionCreators(
            InstitutionItemTemplateCategoriesActions.Clear,
            dispatch
        ),
        filter: bindActionCreators(
            InstitutionItemTemplateCategoriesActions.Filter,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(InstitutionItemTemplateCategorysPage);
